import { useState, type FormEvent } from 'react'
import { Link, useForm } from '@inertiajs/react'
import { motion } from 'framer-motion'
import SeoHead from '@/components/SeoHead'
import MarketingFooter from '@/components/MarketingFooter'

interface BorrowerUserProp {
  id: number
  email: string
}

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
}

// ─── Value prop data ────────────────────────────────────────────────────────

const VALUE_PROPS = [
  {
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
    ),
    title: 'Maturity Countdown',
    description: 'See exactly how many days until each loan matures. Get alerts before deadlines sneak up.',
  },
  {
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
    ),
    title: 'True Cost Breakdown',
    description: 'Points, fees, interest, extensions — see what each loan actually costs, not just the quoted rate.',
  },
  {
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z" />
      </svg>
    ),
    title: 'Multi-Lender View',
    description: 'One dashboard for every private loan. Stop juggling PDFs across 3 different lenders.',
  },
]

// ─── Borrower segments ──────────────────────────────────────────────────────

const BORROWER_SEGMENTS = [
  {
    tag: 'Fix & Flip',
    tagColor: 'bg-orange-100 text-orange-700',
    description: 'Track bridge loans, extension deadlines, and true cost per deal.',
  },
  {
    tag: 'BRRRR Investor',
    tagColor: 'bg-blue-100 text-blue-700',
    description: 'Monitor refinance timelines and compare hard money to long-term debt.',
  },
  {
    tag: 'Small Portfolio',
    tagColor: 'bg-purple-100 text-purple-700',
    description: 'Organize loans by property and see your total monthly obligations.',
  },
  {
    tag: 'Private Money Borrower',
    tagColor: 'bg-teal-100 text-teal-700',
    description: 'Keep every private lender relationship in one place with auto-sync.',
  },
]

// ─── FAQ ────────────────────────────────────────────────────────────────────

const FAQ_ITEMS = [
  {
    question: 'Is this really free?',
    answer: 'Yes. The free tier includes 2 loans with full maturity tracking. Pro and Portfolio tiers unlock AI features, more loans, and property grouping.',
  },
  {
    question: 'What kind of loans can I track?',
    answer: 'Any private loan — bridge loans, hard money, fix-and-flip financing, private mortgages. If you borrowed from a private lender secured by real estate, DebtLens tracks it.',
  },
  {
    question: 'Do I need my lender to sign up too?',
    answer: 'No. You enter your loan details directly. But if your lender uses LendSolo, you can auto-sync balances and payment history.',
  },
  {
    question: 'How is this different from a spreadsheet?',
    answer: "Spreadsheets don't calculate maturity countdowns, true cost breakdowns, or send you alerts. DebtLens does all of this automatically.",
  },
]

const seoSchema = {
  '@graph': [
    {
      '@type': 'WebApplication',
      name: 'DebtLens by LendSolo',
      applicationCategory: 'FinanceApplication',
      description: 'Free dashboard for real estate investors to track private loan obligations, maturity dates, and true borrowing costs across multiple lenders.',
      url: 'https://lendsolo.com/my-loans',
      provider: { '@type': 'Organization', name: 'LendSolo', url: 'https://lendsolo.com' },
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', description: 'Free tier with 2 loans' },
    },
    {
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    },
  ],
}

// ─── Page Component ─────────────────────────────────────────────────────────

interface PageProps {
  borrower_user?: BorrowerUserProp | null
}

export default function MyLoans({ borrower_user }: PageProps) {
  return (
    <>
      <SeoHead
        title="Track Your Private Loans in One Dashboard | DebtLens by LendSolo"
        description="Free dashboard for real estate investors to track private loan obligations, maturity dates, and true borrowing costs across multiple lenders."
        canonicalPath="/my-loans"
        ogType="website"
        schema={seoSchema}
      />

      <div className="min-h-screen">
        <DebtLensLandingHeader loggedIn={!!borrower_user} />
        <HeroSection borrowerUser={borrower_user ?? null} />
        <ValuePropsSection />
        <BorrowerSegmentsSection />
        <HowItWorksSection />
        <FaqSection />
        <FinalCta borrowerUser={borrower_user ?? null} />
        <MarketingFooter variant="debtlens" />
      </div>
    </>
  )
}

// ─── DebtLens Landing Header ────────────────────────────────────────────────

function DebtLensLandingHeader({ loggedIn }: { loggedIn: boolean }) {
  return (
    <header
      className="w-full"
      style={{ background: 'linear-gradient(135deg, var(--color-dl-chrome-from) 0%, var(--color-dl-chrome-to) 100%)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link href="/my-loans" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-dl-accent/15 flex items-center justify-center">
            <svg className="w-4.5 h-4.5 text-dl-accent" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
            </svg>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg font-bold text-white tracking-tight">
              Debt<span className="text-dl-accent">Lens</span>
            </span>
            <span className="text-[10px] text-dl-muted font-medium">by LendSolo</span>
          </div>
        </Link>

        <nav className="flex items-center gap-3">
          <Link href="/blog" className="hidden sm:inline-block text-sm text-dl-muted hover:text-white px-3 py-2 transition-colors">
            Blog
          </Link>
          <Link href="/calculators" className="hidden sm:inline-block text-sm text-dl-muted hover:text-white px-3 py-2 transition-colors">
            Calculators
          </Link>
          {loggedIn ? (
            <Link
              href="/my-loans/dashboard"
              className="text-sm font-semibold text-gray-900 bg-dl-accent hover:bg-dl-accent-hover px-4 py-2 rounded-lg transition-colors"
            >
              Go to Dashboard
            </Link>
          ) : (
            <>
              <Link href="/my-loans/login" className="text-sm text-dl-muted hover:text-white px-3 py-2 transition-colors">
                Log in
              </Link>
              <Link
                href="/my-loans/login"
                className="text-sm font-semibold text-gray-900 bg-dl-accent hover:bg-dl-accent-hover px-4 py-2 rounded-lg transition-colors"
              >
                Get Started Free
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  )
}

// ─── Hero Section ───────────────────────────────────────────────────────────

function HeroSection({ borrowerUser }: { borrowerUser: BorrowerUserProp | null }) {
  const { data, setData, post, processing } = useForm({ email: '' })
  const [emailSent, setEmailSent] = useState(false)
  const [sentEmail, setSentEmail] = useState('')

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const email = data.email.trim()
    if (!email) return
    setSentEmail(email)
    post('/my-loans/login', { onSuccess: () => setEmailSent(true) })
  }

  return (
    <section style={{ background: 'linear-gradient(165deg, var(--color-dl-chrome-from) 0%, var(--color-dl-chrome-to) 100%)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <motion.div className="max-w-2xl" {...fadeInUp}>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-dl-accent/15 text-dl-accent border border-dl-accent/25">
            Free debt dashboard
          </span>

          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
            One dashboard for all your private loans
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-gray-300 font-body leading-relaxed">
            Track maturity dates, true borrowing costs, and payment history across every lender.
            Built for real estate investors who borrow from private lenders.
          </p>

          {borrowerUser ? (
            <div className="mt-8">
              <Link
                href="/my-loans/dashboard"
                className="inline-block px-8 py-3 rounded-lg text-base font-semibold text-gray-900 bg-dl-accent hover:bg-dl-accent-hover transition-colors"
              >
                Go to your Dashboard
              </Link>
              <p className="mt-3 text-sm text-gray-400">Logged in as {borrowerUser.email}</p>
            </div>
          ) : emailSent ? (
            <div className="mt-8 rounded-lg bg-dl-accent/15 border border-dl-accent/25 p-4">
              <p className="text-dl-accent font-medium">
                Check your email! We sent a login link to {sentEmail}.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-8 flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={data.email}
                onChange={(e) => setData('email', e.target.value)}
                placeholder="you@example.com"
                required
                className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dl-accent focus:border-transparent"
              />
              <button
                type="submit"
                disabled={processing}
                className="px-8 py-3 rounded-lg text-base font-semibold text-gray-900 bg-dl-accent hover:bg-dl-accent-hover transition-colors disabled:opacity-50"
              >
                {processing ? 'Sending...' : 'Log in free'}
              </button>
            </form>
          )}
          {!borrowerUser && !emailSent && (
            <p className="mt-3 text-sm text-gray-400">No password needed. We'll email you a secure login link.</p>
          )}
        </motion.div>
      </div>
    </section>
  )
}

// ─── Value Props Section ────────────────────────────────────────────────────

function ValuePropsSection() {
  return (
    <section className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="text-center" {...fadeInUp}>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-gray-900">
            Everything you need to stay on top of your loans
          </h2>
          <p className="mt-4 text-lg text-gray-600 font-body max-w-2xl mx-auto">
            Private loan borrowers juggle multiple lenders, scattered documents, and surprise maturity dates. DebtLens puts it all in one place.
          </p>
        </motion.div>

        <div className="mt-14 grid gap-6 sm:grid-cols-3">
          {VALUE_PROPS.map((prop, i) => (
            <motion.div
              key={prop.title}
              className="bg-gray-50 rounded-2xl p-6 border border-gray-100"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <div className={`w-10 h-10 rounded-xl ${prop.iconBg} ${prop.iconColor} flex items-center justify-center`}>
                {prop.icon}
              </div>
              <h3 className="mt-4 text-lg font-bold text-gray-900">{prop.title}</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">{prop.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── Borrower Segments Section ──────────────────────────────────────────────

function BorrowerSegmentsSection() {
  return (
    <section className="py-16 sm:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="text-center" {...fadeInUp}>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
            Built for how you invest
          </h2>
        </motion.div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {BORROWER_SEGMENTS.map((segment, i) => (
            <motion.div
              key={segment.tag}
              className="bg-white rounded-xl p-5 border border-gray-200"
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
            >
              <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${segment.tagColor}`}>
                {segment.tag}
              </span>
              <p className="mt-3 text-sm text-gray-600 leading-relaxed">{segment.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div className="mt-10 text-center" {...fadeInUp}>
          <p className="text-sm text-gray-500">
            Trusted by real estate investors managing private debt &middot; Free tier includes 2 loans
          </p>
        </motion.div>
      </div>
    </section>
  )
}

// ─── How It Works Section ───────────────────────────────────────────────────

function HowItWorksSection() {
  const steps = [
    { number: '1', title: 'Add your loans', description: "Enter each loan's principal, rate, term, fees, and lender. Takes about 2 minutes per loan." },
    { number: '2', title: 'See the full picture', description: 'DebtLens calculates your true costs, tracks maturity timelines, and organizes everything by lender.' },
    { number: '3', title: 'Stay ahead', description: 'Get maturity alerts and cost comparisons so you never miss a deadline or overpay on an extension.' },
  ]

  return (
    <section className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="text-center" {...fadeInUp}>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-gray-900">How it works</h2>
        </motion.div>

        <div className="mt-14 grid gap-8 sm:grid-cols-3">
          {steps.map((step, i) => (
            <motion.div
              key={step.number}
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <div
                className="mx-auto w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold text-gray-900"
                style={{ backgroundColor: 'var(--color-dl-accent)' }}
              >
                {step.number}
              </div>
              <h3 className="mt-4 text-lg font-bold text-gray-900">{step.title}</h3>
              <p className="mt-2 text-gray-600">{step.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── FAQ Section ────────────────────────────────────────────────────────────

function FaqSection() {
  const [open, setOpen] = useState<number | null>(null)

  return (
    <section className="py-16 sm:py-24 bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="text-center" {...fadeInUp}>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-gray-900">Frequently asked questions</h2>
        </motion.div>

        <div className="mt-10 space-y-3">
          {FAQ_ITEMS.map((item, i) => (
            <motion.div
              key={i}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between px-5 py-4 text-left"
              >
                <span className="font-semibold text-gray-900">{item.question}</span>
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform ${open === i ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
              </button>
              {open === i && (
                <div className="px-5 pb-4 text-gray-600 leading-relaxed">{item.answer}</div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── Final CTA ──────────────────────────────────────────────────────────────

function FinalCta({ borrowerUser }: { borrowerUser: BorrowerUserProp | null }) {
  const { data, setData, post, processing } = useForm({ email: '' })
  const [emailSent, setEmailSent] = useState(false)

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!data.email.trim()) return
    post('/my-loans/login', { onSuccess: () => setEmailSent(true) })
  }

  return (
    <section style={{ background: 'linear-gradient(165deg, var(--color-dl-chrome-from) 0%, var(--color-dl-chrome-to) 100%)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
        <motion.div {...fadeInUp}>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
            Start tracking your loans &mdash; free forever
          </h2>
          <p className="mt-4 text-lg text-gray-300 font-body max-w-xl mx-auto">
            No password, no credit card. Enter your email and start tracking your private loans in one place.
          </p>

          {borrowerUser ? (
            <div className="mt-8">
              <Link
                href="/my-loans/dashboard"
                className="inline-block px-8 py-3 rounded-lg text-base font-semibold text-gray-900 bg-dl-accent hover:bg-dl-accent-hover transition-colors"
              >
                Go to your Dashboard
              </Link>
            </div>
          ) : emailSent ? (
            <div className="mt-8 mx-auto max-w-md rounded-lg bg-dl-accent/15 border border-dl-accent/25 p-4">
              <p className="text-dl-accent font-medium">Check your email for a login link!</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                value={data.email}
                onChange={(e) => setData('email', e.target.value)}
                placeholder="you@example.com"
                required
                className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dl-accent focus:border-transparent"
              />
              <button
                type="submit"
                disabled={processing}
                className="px-8 py-3 rounded-lg text-base font-semibold text-gray-900 bg-dl-accent hover:bg-dl-accent-hover transition-colors disabled:opacity-50"
              >
                {processing ? 'Sending...' : 'Log in free'}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  )
}

